import { useEffect, useState } from 'react';
import Button from '../../components/button/Button';
import LinkButton from '../../components/button/LinkButton';
import InputField from '../../components/inputField/InputField';
import ScreenContainer from '../../components/layout/ScreenContainer';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { SignupAPI } from '../../logic/signupApi/signupApi';
import { auth } from '../../FirebaseConfig';
import {
  ConfirmationResult, PhoneAuthProvider,
  signInWithCredential,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  setPersistence,
  EmailAuthProvider,
  inMemoryPersistence, getMultiFactorResolver,
  MultiFactorResolver,
  TotpMultiFactorGenerator
} from "firebase/auth";
import ErrorMessage from '../../components/errorMessage/ErrorMessage';
import Message from '../../components/message/Message';
import Loader from '../../components/loader/Loader';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/modal/Modal';
import { getOrCreateRecaptchaVerifier } from '../../utils/recaptchaUtils';
import { formatPhoneNumber } from '../../utils/phoneNumberUtils';

const VerifyCodeScreen = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors }, setFocus } = useForm();
  // The 2FA modal is a separate form; keeping its own instance means its required
  // `totp_code` field can never block validation of the main verification-code form.
  const {
    register: register2FA,
    handleSubmit: handleSubmit2FA,
    formState: { errors: errors2FA },
    setFocus: setFocus2FA,
  } = useForm();

  console.log("Verification:", useLocation().state);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const email = useLocation().state?.email || null;
  const phone = useLocation().state?.phone || null;
  const phoneConfirmation = useLocation().state?.phoneConfirmation || null;
  const verificationMethod = useLocation().state?.verificationMethod || null; // 'email', 'sms', or 'voice'
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoading2FA, setIsLoading2FA] = useState<boolean>(false);

  const [isVerifyingByPhone, setIsVerifyingByPhone] = useState<boolean>(false);
  const [isVerifyingByVoice, setIsVerifyingByVoice] = useState<boolean>(false);
  const [phoneConfirmationState, setPhoneConfirmationState] = useState<ConfirmationResult | null>(phoneConfirmation ? JSON.parse(phoneConfirmation) : null);

  const handleLoginErrors = (error: any) => {
    setIsLoading(false);
    console.log('error while trying to login with email - google:', error);

    switch (error.code) {
      case "auth/too-many-requests":
        setErrMsgTxt(t('TOO_MANY_FAILED_ATTEMPTS'))
        break;
      case "auth/multi-factor-auth-required":
        setErrMsgTxt(t('ACCOUNT_HAS_2FA'));
        try {
          const mfaResolver = getMultiFactorResolver(auth, error)
          setResolver(mfaResolver);
          setIsModalOpen(true);
        } catch (error) {
          setErrMsgTxt(t('FIREBASE_INTERNAL_ERROR'))
          console.log('firebase error', error);
        }
        break;
      default:
        setErrMsgTxt(t('WRONG_VERIFICATION_CODE'));
        setFocus('code');
    }
    setErrMsgVisible(true);
  }

  const signInWithEmail = (data: any) => {
    setIsLoading(true);

    setPersistence(auth, inMemoryPersistence)
    const credentials = EmailAuthProvider.credential(email, data.code?.trim());

    signInWithCredential(auth, credentials).then(async (result: any) => {

      const idToken = await result.user.getIdToken(true);
      return await SignupAPI.sessionLogin({ idToken, language: i18n.language }).catch((error: unknown) => {
        console.log('error while trying to login with email - server: ', error);
        setIsLoading(false);
      });
    })
      .then(async (data) => {
        setIsLoading(false);
        await auth.signOut();
        return data;
      })
      .then((data) => {
        navigate('/login/verification-complete', { state: { roles: data.roles } });
      })
      .catch((error: any) => {
        handleLoginErrors(error);
        setIsLoading(false);
      });
  }

  const signInWithPhone = (data: any) => {
    if (!phoneConfirmationState) {
      setErrMsgTxt(t('PHONE_CONFIRMATION_NOT_AVAILABLE'));
      setErrMsgVisible(true);
      return;
    }

    setIsLoading(true);

    const confirmation: ConfirmationResult = phoneConfirmationState;
    const credentials = PhoneAuthProvider.credential(confirmation.verificationId, data.code?.trim());

    signInWithCredential(auth, credentials).then(async (result: any) => {
      const idToken = await result.user?.getIdToken(true);
      return await SignupAPI.sessionLogin({ idToken, language: i18n.language }).catch((error: unknown) => {
        console.log('error while trying to login with phone - server: ', error);
        setIsLoading(false);
      })
    })
      .then(async (data) => {
        setIsLoading(false);
        await auth.signOut();
        return data;
      })
      .then((data) => {
        navigate('/login/verification-complete', { state: { roles: data.roles } });
      }).catch((error: any) => {
        handleLoginErrors(error);
        setIsLoading(false);
      });
  }

  const signInWithVoice = async (data: any) => {
    setIsLoading(true);

    try {
      // First, verify the code with Twilio
      await SignupAPI.verifyTwilioCode({
        email: email,
        phone: phone,
        code: data.code?.trim()
      });

      // If Twilio verification succeeds, the Firebase password has been updated to the code
      // Now authenticate with Firebase using that code
      setPersistence(auth, inMemoryPersistence);
      const credentials = EmailAuthProvider.credential(email, data.code?.trim());

      signInWithCredential(auth, credentials).then(async (result: any) => {
        const idToken = await result.user.getIdToken(true);
        return await SignupAPI.sessionLogin({ idToken, language: i18n.language }).catch((error: unknown) => {
          console.log('error while trying to login with voice - server: ', error);
          setIsLoading(false);
        });
      })
        .then(async (data) => {
          setIsLoading(false);
          await auth.signOut();
          return data;
        })
        .then((data) => {
          navigate('/login/verification-complete', { state: { roles: data.roles } });
        })
        .catch((error: any) => {
          handleLoginErrors(error);
          setIsLoading(false);
        });
    } catch (error: any) {
      setIsLoading(false);
      setErrMsgTxt(error.message || t('INVALID_VERIFICATION_CODE_RETRY'));
      setErrMsgVisible(true);
      setFocus('code');
    }
  }

  const onSubmit = (data: any) => {
    if (isVerifyingByVoice) {
      signInWithVoice(data);
    } else if (isVerifyingByPhone) {
      signInWithPhone(data);
    }
    else {
      signInWithEmail(data);
    }
  };

  const submit2FA = async (data: any) => {
    const otpFromAuthenticator = data.totp_code;

    if (!resolver) {
      setErrMsgTxt('Failed to resolve multi-factor authentication.');
      return;
    }

    const resolverUID = resolver.hints.filter(value => value.factorId === 'totp')[0]?.uid;

    if (!resolverUID) {
      setErrMsgTxt('Failed to resolve multi-factor authentication.');
      return;
    }

    setIsLoading2FA(true);

    const multiFactorAssertion =
      TotpMultiFactorGenerator.assertionForSignIn(
        resolverUID,
        otpFromAuthenticator
      );
    await resolver.resolveSignIn(multiFactorAssertion).then(async (result) => {
      const idToken = await result.user.getIdToken(true);
      const sessionData = await SignupAPI.sessionLogin({ idToken, language: i18n.language }).catch((error: unknown) => {
        console.log('error while trying to login with email - server: ', error);
        setErrMsgTxt(t('SESSION_COOKIE_ERROR'));
        setIsLoading2FA(false);
      });

      await SignupAPI.totpSessionLogin({ idToken }).catch((error: unknown) => {
        console.log('error while trying to login with 2fa - server: ', error);
        setErrMsgTxt(t('SECOND_FACTOR_COOKIE_ERROR'));
        setIsLoading2FA(false);
      });

      return sessionData;
    })
      .then(async (data) => {
        setIsLoading2FA(false);
        await auth.signOut();
        return data;
      })
      .then((data) => {
        navigate('/login/verification-complete', { state: { roles: data.roles } });
      })
      .catch((error) => {
        console.log('Error checking code: ', error)
        setErrMsgTxt(t('WRONG_VERIFICATION_CODE'));
        setIsLoading2FA(false);
      });
  }

  useEffect(() => {
    if (!phone && !email) {
      navigate('/login/');
    }

    // Set verification method based on state
    if (verificationMethod === 'voice') {
      setIsVerifyingByVoice(true);
      setIsVerifyingByPhone(false);
    } else if (verificationMethod === 'sms' || (phone && !email)) {
      setIsVerifyingByPhone(true);
      setIsVerifyingByVoice(false);
    } else {
      setIsVerifyingByPhone(false);
      setIsVerifyingByVoice(false);
    }
  }, [phone, email, verificationMethod]);

  useEffect(() => {
    if (isVerifyingByPhone || (phone && !isVerifyingByVoice && !email)) {
      try {
        getOrCreateRecaptchaVerifier('recaptcha-container-resend');
      } catch (error) {
        console.error('Error initializing RecaptchaVerifier:', error);
      }
    }

    // Clean up when the component unmounts or method changes
    return () => {
      if (window.recaptchaVerifier && !(window.recaptchaVerifier as any).destroyed) {
        try {
          window.recaptchaVerifier?.clear();
        } catch (e) {
          console.error("Cleanup error", e);
        }
      }
    };
  }, [isVerifyingByPhone, phone, isVerifyingByVoice, email]);

  useEffect(() => {
    if (isModalOpen) {
      setFocus2FA('totp_code');
    }
    else {
      setFocus('code');
    }
  }, [isModalOpen]);

  const resendEmailCode = () => {
    setIsLoading(true);
    SignupAPI.sendLoginCode({
      whereToSendTheCode: 'email',
      email: email,
      language: i18n.language
    })
      .then(() => {
        setIsLoading(false);
        setResendCodeMsgVisible(true);
      })
      .catch((error: unknown) => {
        setIsLoading(false);
        console.log('error while sending login code: ', error)
      });

  };

  const resendPhoneCode = async () => {
    setIsLoading(true);

    // Small delay to ensure any previous reCAPTCHA activity is settled
    await new Promise(resolve => setTimeout(resolve, 100));

    // Get or create recaptcha verifier
    let appVerifier: RecaptchaVerifier;
    try {
      appVerifier = getOrCreateRecaptchaVerifier('recaptcha-container-resend');
    } catch (error: any) {
      setIsLoading(false);
      setErrMsgTxt(error.message || t('VERIFICATION_SYSTEM_NOT_READY'));
      setErrMsgVisible(true);
      return;
    }

    signInWithPhoneNumber(auth, phone, appVerifier)
      .then((confirmationResult) => {
        setIsLoading(false);
        setResendCodeMsgVisible(true);
        setPhoneConfirmationState(confirmationResult);
      }).catch((error) => {
        setIsLoading(false);
        console.log("SMS Error: ", error);
      });
  }

  const resendVoiceCode = () => {
    setIsLoading(true);
    SignupAPI.sendLoginCode({
      whereToSendTheCode: 'voice',
      email: email,
      telephoneMobile: phone,
      language: i18n.language
    })
      .then(() => {
        setIsLoading(false);
        setResendCodeMsgVisible(true);
        setIsVerifyingByVoice(true);
      })
      .catch((error: unknown) => {
        setIsLoading(false);
        console.log('error while sending voice call code: ', error)
        setErrMsgTxt(t('COULD_NOT_SEND_VOICE_CALL'));
        setErrMsgVisible(true);
      });
  }

  const resendCode = () => {
    if (isVerifyingByVoice) {
      resendVoiceCode();
    } else if (isVerifyingByPhone) {
      resendPhoneCode();
    }
    else {
      resendEmailCode();
    }
  }

  const resentCodeText = () => {
    if (isVerifyingByVoice) {
      return t('RESEND_CODE_VOICE');
    } else if (isVerifyingByPhone) {
      return t('RESEND_CODE_TEXT');
    }
    else {
      return t('RESEND_CODE_EMAIL');
    }
  }

  const switchToEmail = async () => {
    setIsLoading(true);
    setIsVerifyingByVoice(false);
    setIsVerifyingByPhone(false);
    try {
      await SignupAPI.sendLoginCode({
        whereToSendTheCode: 'email',
        email: email,
        language: i18n.language
      });
      setIsLoading(false);
      setResendCodeMsgVisible(true);
    } catch (error: unknown) {
      setIsLoading(false);
      console.log('error while sending email code: ', error);
      setErrMsgTxt(t('COULD_NOT_SEND_EMAIL_CODE'));
      setErrMsgVisible(true);
    }
  }

  const switchToSMS = async () => {
    if (!phone) {
      setErrMsgTxt(t('PHONE_REQUIRED_FOR_SMS'));
      setErrMsgVisible(true);
      return;
    }
    setIsLoading(true);
    setIsVerifyingByVoice(false);
    setIsVerifyingByPhone(true);

    // Small delay to ensure any previous reCAPTCHA activity is settled
    await new Promise(resolve => setTimeout(resolve, 100));

    // Get or create recaptcha verifier
    let appVerifier: RecaptchaVerifier;
    try {
      appVerifier = getOrCreateRecaptchaVerifier('recaptcha-container-resend');
    } catch (error: any) {
      setIsLoading(false);
      setErrMsgTxt(error.message || t('VERIFICATION_SYSTEM_NOT_READY'));
      setErrMsgVisible(true);
      return;
    }

    try {
      const confirmationResult = await signInWithPhoneNumber(auth, phone, appVerifier);
      setIsLoading(false);
      setResendCodeMsgVisible(true);
      setPhoneConfirmationState(confirmationResult);
    } catch (error) {
      setIsLoading(false);
      console.log("SMS Error: ", error);
      setErrMsgTxt(t('COULD_NOT_SEND_SMS_CODE'));
      setErrMsgVisible(true);
    }
  }

  const switchToVoice = async () => {
    if (!phone || !email) {
      setErrMsgTxt(t('PHONE_AND_EMAIL_REQUIRED_FOR_VOICE'));
      setErrMsgVisible(true);
      return;
    }
    setIsLoading(true);
    setIsVerifyingByVoice(true);
    setIsVerifyingByPhone(false);
    try {
      await SignupAPI.sendLoginCode({
        whereToSendTheCode: 'voice',
        email: email,
        telephoneMobile: phone,
        language: i18n.language
      });
      setIsLoading(false);
      setResendCodeMsgVisible(true);
    } catch (error: unknown) {
      setIsLoading(false);
      console.log('error while sending voice call code: ', error);
      setErrMsgTxt(t('COULD_NOT_SEND_VOICE_CALL'));
      setErrMsgVisible(true);
    }
  }

  const [errMsgVisible, setErrMsgVisible] = useState(false);
  const [errMsgTxt, setErrMsgTxt] = useState('');

  const [resendCodeMsgVisible, setResendCodeMsgVisible] = useState(false)

  return (
    <ScreenContainer pageTitle={t('PAGE_TITLE_VERIFY_CODE')}>
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={t('CODE_FROM_APP')}
        >
          <form onSubmit={handleSubmit2FA(submit2FA)}>
            <div className="form-group u-mb-1">
              <p className="u-mb-1">{t('CODE_FROM_APP_INSTRUCTIONS')}</p>
            </div>
            <InputField
              name="totp_code"
              errors={errors2FA}
              register={register2FA}
              large
              options={{
                required: true,
                validate: {
                  minLength: (v) => v.length >= 0,
                }
              }}
              placeholder={t('CODE_FROM_APP')}
            />
            {isLoading2FA && <Loader />}
            <Button title={t('FINISH')} />
          </form>
        </Modal>

        {
          errMsgVisible && <ErrorMessage message={errMsgTxt} onClose={() => { setErrMsgVisible(false); }} />
        }
        {resendCodeMsgVisible &&
          <Message onClose={() => setResendCodeMsgVisible(false)}
            title={t('NEW_CODE_SENT')}
            children={
              <>
                {!isVerifyingByPhone && !isVerifyingByVoice && <span>{t('VERIFY_CODE_SCREEN_TEXT_FIRST')}</span>}
              </>
            } />
        }
        <h2 className='size-h2 u-mb-1 u-text-bold'>{t('ALMOST_DONE')}</h2>
        <p className='large u-mb-2'>{t('CODE_SENT_TO', { destination: isVerifyingByVoice ? formatPhoneNumber(phone) + t('VOICE_CALL_SUFFIX') : isVerifyingByPhone ? formatPhoneNumber(phone) : email })}</p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputField
            name="code"
            errors={errors}
            register={register}
            large
            options={{
              required: true,
              validate: {
                minLength: (v) => v.length >= 0,
              }
            }}
            placeholder={t('ENTER_THE_CODE_HERE')}
          />
          {isLoading && <Loader />}
          <Button title={t('CONTINUE')} />
          {/* Show options based on current verification method */}
          <div id="recaptcha-container-resend"></div>
          <LinkButton block title={resentCodeText()} onClick={() => resendCode()} />

          {!isVerifyingByPhone && !isVerifyingByVoice && email && (
            <LinkButton
              block
              title={t('EMAIL_SCREEN_TEXT_FIRST')}
              onClick={() => navigate(`/login/phone`, { state: { email: email, phone } })}
            />
          )}

          {/* If verifying by voice: show SMS and Email options */}
          {isVerifyingByVoice && email && (
            <>
              {phone && (
                <LinkButton block title={t('VERIFY_BY_SMS_INSTEAD')} onClick={switchToSMS} disabled={isLoading} />
              )}
              <LinkButton block title={t('PHOHE_SCREEN_TEXT_THIRD')} onClick={switchToEmail} disabled={isLoading} />
            </>
          )}

          {/* If verifying by SMS: show Voice and Email options */}
          {isVerifyingByPhone && email && (
            <>
              <LinkButton block title={t('RECEIVE_VOICE_CALL_INSTEAD')} onClick={switchToVoice} disabled={isLoading} />
              <LinkButton block title={t('PHOHE_SCREEN_TEXT_THIRD')} onClick={switchToEmail} disabled={isLoading} />
            </>
          )}
        </form>
    </ScreenContainer>
  )
};

export default VerifyCodeScreen